import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import type { CategoryRangeFilter } from 'graphql-ts-types';

import type { RangeSliderElement, RangeSliderInteraction } from '../../tracking/filters';
import { trackRangeSliderApply, trackRangeSliderChange } from '../../tracking/filters';
import { Flex } from '../Flex';
import RangeSlider from '../RangeSlider';
import { BodyRegularBR1 } from '../Typography';

export interface FilterRangeSectionProps extends Omit<
  CategoryRangeFilter,
  'currentMin' | 'currentMax'
> {
  filterKey: string;
  withApplyButton?: boolean;
  onChange: (
    filterKey: string,
    values: { newMin: number; newMax: number; min: number; max: number }
  ) => void;
  withTitle?: boolean;
}

const rangeContainerStyles: ComponentProps<typeof Flex> = {
  flexDirection: 'column',
  px: 'sp_16',
  py: 'sp_0',
  pb: 'sp_24',
};

const RangeContainer = (props: ComponentProps<typeof Flex>) => (
  <Flex {...rangeContainerStyles} {...props} />
);

const FilterRangeSection = ({
  filterKey,
  onChange,
  withApplyButton,
  withTitle,
  ...rest
}: FilterRangeSectionProps) => {
  const onRangeChange = useCallback(
    ([newMin, newMax]: number[]) => {
      onChange(filterKey, {
        newMin,
        newMax,
        min: rest.min,
        max: rest.max,
      });
      if (withApplyButton) {
        trackRangeSliderApply(filterKey);
      }
    },
    [filterKey, rest.min, rest.max, withApplyButton, onChange]
  );

  const trackRangeChange = useCallback(
    (element: RangeSliderElement, interaction: RangeSliderInteraction) => {
      trackRangeSliderChange({
        label: filterKey,
        element,
        interaction,
        isDrawer: !withApplyButton,
      });
    },
    [filterKey, withApplyButton]
  );

  return (
    <RangeContainer {...(withTitle ? { mb: 'sp_24' } : {})}>
      {withTitle && <BodyRegularBR1 mb="sp_8">{rest.name}</BodyRegularBR1>}
      <RangeSlider
        {...rest}
        withApplyButton={withApplyButton}
        onRangeChange={onRangeChange}
        trackRangeChange={trackRangeChange}
      />
    </RangeContainer>
  );
};

export default FilterRangeSection;
